import React from 'react';

interface Props {
  children: React.ReactNode;
}
interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary global qui empêche l'écran noir bloqué.
 * Si n'importe quel composant React crash, on affiche un message
 * propre avec les détails de l'erreur (pour débug terrain).
 */
export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log conservé (transform-remove-console garde error/warn)
    console.error('[ErrorBoundary] App crashed:', error, info);
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_) {}
  };

  handleResetData = async () => {
    if (!window.confirm("Réinitialiser TOUTES les données locales ? Cette action est irréversible.")) {
      return;
    }
    try {
      // Effacer localStorage + IndexedDB de l'app
      localStorage.clear();
      sessionStorage.clear();
      // Effacer toutes les bases IndexedDB connues
      const dbs = await indexedDB.databases?.() || [];
      await Promise.all(
        dbs.map((db) => {
          if (db.name) {
            return new Promise<void>((resolve) => {
              const req = indexedDB.deleteDatabase(db.name!);
              req.onsuccess = req.onerror = req.onblocked = () => resolve();
            });
          }
          return Promise.resolve();
        })
      );
      window.location.reload();
    } catch (e) {
      console.error('Reset error:', e);
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
            background: '#fff',
            color: '#111827',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            zIndex: 99999,
            overflow: 'auto',
          }}
        >
          <div style={{ maxWidth: 600, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              Une erreur est survenue
            </h1>
            <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
              L'application a rencontré un problème. Vous pouvez recharger la page ou réinitialiser les données.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  background: '#f97316',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                🔄 Recharger la page
              </button>
              <button
                onClick={this.handleResetData}
                style={{
                  padding: '12px 24px',
                  background: '#fff',
                  color: '#dc2626',
                  border: '1px solid #dc2626',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                🗑️ Réinitialiser les données
              </button>
            </div>
            {this.state.error && (
              <details
                style={{
                  marginTop: 24,
                  textAlign: 'left',
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 12,
                  color: '#991b1b',
                }}
              >
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Détails techniques
                </summary>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 8 }}>
                  {this.state.error.name}: {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default AppErrorBoundary;
